import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormLabel,
  Input,
  Select,
  Textarea,
  Box,
} from '@chakra-ui/react';
import { Task } from '../../types/task';
import { useGetAllEmployeesQuery } from '../../features/apis/authApi';
import { useGetAllProjectsQuery } from '../../features/apis/taskManager';
import useCurrentUser from '../../hooks/useCurrentUser';

export interface CreateTaskProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (task: Task) => Promise<void>;
  isLoading: boolean;
  selectedDate: Date;
  isEmployee?: boolean;
  assignedTo?: string;
  assignedBy?: string;
}

const CreateTask: React.FC<CreateTaskProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  selectedDate,
  isEmployee = false,
  assignedTo,
  assignedBy,
}) => {
  const today = new Date().toISOString().split('T')[0];

  const { data: employeeData } = useGetAllEmployeesQuery();
  const { data: projectData } = useGetAllProjectsQuery();
  const user = useCurrentUser();

  const [taskData, setTaskData] = useState<Task>({
    name: '',
    status: '',
    dependency: '',
    timeRequired: '',
    description: '',
    assignedDate: today,
    dueDate: today,
    assignedTo: '',
    assignedBy: '',
    projectId: '',
  });

  const initialized = useRef(false);

  useEffect(() => {
    if (selectedDate && !initialized.current) {
      const dateString = selectedDate.toISOString().split('T')[0];
      setTaskData((prev) => ({
        ...prev,
        assignedDate: dateString,
        dueDate: dateString,
      }));
      initialized.current = true;
    }
  }, [selectedDate]);

  useEffect(() => {
    if (isEmployee && user?._id) {
      setTaskData((prev) => ({
        ...prev,
        assignedTo: user._id,
        assignedBy: user._id,
      }));
    }
  }, [isEmployee, user]);

  useEffect(() => {
    if (!isEmployee) {
      setTaskData((prev) => ({
        ...prev,
        ...(assignedTo ? { assignedTo } : {}),
        ...(assignedBy ? { assignedBy } : {}),
      }));
    }
  }, [isEmployee, assignedTo, assignedBy, isOpen]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    if (name === 'dependency' && value === 'Other') {
      setTaskData((prev) => ({ ...prev, dependency: '' }));
    } else {
      setTaskData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    try {
      await onSubmit(taskData);
      setTaskData({
        name: '',
        status: '',
        dependency: '',
        timeRequired: '',
        description: '',
        assignedDate: today,
        dueDate: today,
        assignedTo: '',
        assignedBy: '',
        projectId: '',
      });
      onClose();
    } catch (err) {
      console.error('Task submission failed:', err);
    }
  };

  const isFormComplete = Object.values(taskData)
    .filter((value) => typeof value === 'string')
    .every((value) => value.trim() !== '');

  const employees = employeeData?.employees ?? [];
  const validEmployees = employees.filter(
    (employee) => employee.name && employee.name.trim() !== ''
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay bg="blackAlpha.200" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="20px" p={4}>
        <ModalHeader fontSize="28px" fontWeight="700">
          Add New Task
          <hr className="border-t-[0.25px] border-[#D4D4D4] mt-1" />
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box mb={2}>
              <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                Task
              </FormLabel>
              <Input
                name="name"
                value={taskData.name}
                onChange={handleChange}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
                h="48px"
                fontSize="11px"
              />
            </Box>

            <Box mb={2}>
              <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                Status
              </FormLabel>
              <Select
                name="status"
                value={taskData.status}
                onChange={handleChange}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
                h="48px"
                fontSize="11px"
              >
                <option value="">Select Status</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </Select>
            </Box>

            <Box mb={2}>
              <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                Dependency
              </FormLabel>
              <Select
                name="dependency"
                value={taskData.dependency}
                onChange={handleChange}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
                h="48px"
                fontSize="11px"
              >
                <option value="">Select an Employee</option>
                {validEmployees.map((employee) => (
                  <option key={employee._id} value={employee._id}>
                    {employee.name}
                  </option>
                ))}
                <option value="Other">Other</option>
              </Select>
            </Box>

            {!isEmployee && (
              <>
                <Box mb={2}>
                  <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                    Assigned To
                  </FormLabel>
                  <Select
                    name="assignedTo"
                    value={taskData.assignedTo}
                    onChange={handleChange}
                    border="0.5px solid"
                    borderColor="#D4D4D4"
                    _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                    _hover={{ borderColor: '#D4D4D4' }}
                    h="48px"
                    fontSize="11px"
                  >
                    <option value="">Select Employee</option>
                    {validEmployees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {employee.name}
                      </option>
                    ))}
                  </Select>
                </Box>

                <Box mb={2}>
                  <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                    Assigned By
                  </FormLabel>
                  <Select
                    name="assignedBy"
                    value={taskData.assignedBy}
                    onChange={handleChange}
                    border="0.5px solid"
                    borderColor="#D4D4D4"
                    _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                    _hover={{ borderColor: '#D4D4D4' }}
                    h="48px"
                    fontSize="11px"
                  >
                    <option value="">Select Employee</option>
                    {validEmployees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {employee.name}
                      </option>
                    ))}
                  </Select>
                </Box>
              </>
            )}

            <Box mb={2}>
              <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                Project
              </FormLabel>
              <Select
                name="projectId"
                value={taskData.projectId}
                onChange={handleChange}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
                h="48px"
                fontSize="11px"
              >
                <option value="">Select Project</option>
                {projectData?.projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </Select>
            </Box>

            <Box mb={2}>
              <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                Time Required
              </FormLabel>
              <Input
                type="number"
                name="timeRequired"
                value={taskData.timeRequired}
                onChange={handleChange}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
                h="48px"
                fontSize="11px"
              />
            </Box>

            <Box mb={2}>
              <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                Assigned Date
              </FormLabel>
              <Input
                type="date"
                name="assignedDate"
                value={taskData.assignedDate}
                onChange={handleChange}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
                h="48px"
                fontSize="11px"
              />
            </Box>

            <Box mb={2}>
              <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                Due Date
              </FormLabel>
              <Input
                type="date"
                name="dueDate"
                value={taskData.dueDate}
                onChange={handleChange}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
                h="48px"
                fontSize="11px"
              />
            </Box>

            <Box mb={2} gridColumn="span 2">
              <FormLabel fontSize="12px" fontWeight="bold" mb={1}>
                Description
              </FormLabel>
              <Textarea
                name="description"
                value={taskData.description}
                onChange={handleChange}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
                fontSize="11px"
              />
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter justifyContent="flex-end">
          <Button
            borderRadius="6px"
            size="sm"
            bgGradient="linear(to-r, #6535EB, #9F3198)"
            color="white"
            height={{ base: '26px', md: '34px' }}
            px={{ base: 3, md: 5 }}
            fontSize={{ base: '10px', md: '12px' }}
            fontWeight="500"
            whiteSpace="nowrap"
            transition="all 0.2s ease"
            _hover={{
              transform: 'translateY(-1px)',
              bgGradient: 'linear(to-r, #5a2fe0, #8a2a85)',
            }}
            _active={{
              transform: 'scale(0.98)',
            }}
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Saving..."
            isDisabled={!isFormComplete || isLoading}
          >
            Create Task
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateTask;
